import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { ISousMatiere, defaultValue } from 'app/shared/model/sous-matiere.model';

const initialState: EntityState<ISousMatiere> = {
  loading: false,
  errorMessage: null,
  entities: [] as ReadonlyArray<ISousMatiere>,
  entity: defaultValue,
  updating: false,
  updateSuccess: false,
  totalItems: 0,
};

const apiUrl = 'api/sous-matieres';

export const getSousMatieres = createAsyncThunk(
  'sousMatiere/fetch_list',
  async ({ cycleMatiereId, cycleId, matiereEcoleId }: { cycleMatiereId?: string; cycleId?: string; matiereEcoleId?: string }) => {
    const params = new URLSearchParams();
    if (cycleMatiereId) params.set('cycleMatiereId', cycleMatiereId);
    if (cycleId) params.set('cycleId', cycleId);
    if (matiereEcoleId) params.set('matiereEcoleId', matiereEcoleId);
    const requestUrl = `${apiUrl}${params.toString() ? `?${params.toString()}` : ''}`;
    return axios.get<ISousMatiere[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createSousMatiere = createAsyncThunk(
  'sousMatiere/create_entity',
  async (entity: ISousMatiere, thunkAPI) => {
    const result = await axios.post<ISousMatiere>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getSousMatieres({ cycleMatiereId: entity.cycleMatiereId }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateSousMatiere = createAsyncThunk(
  'sousMatiere/update_entity',
  async (entity: ISousMatiere, thunkAPI) => {
    const result = await axios.put<ISousMatiere>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getSousMatieres({ cycleMatiereId: entity.cycleMatiereId }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteSousMatiere = createAsyncThunk(
  'sousMatiere/delete_entity',
  async (entity: ISousMatiere, thunkAPI) => {
    const result = await axios.delete<ISousMatiere>(`${apiUrl}/${entity.id}`);
    thunkAPI.dispatch(getSousMatieres({ cycleMatiereId: entity.cycleMatiereId }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const SousMatiereSlice = createSlice({
  name: 'sousMatiere',
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addMatcher(isFulfilled(getSousMatieres), (state, action) => {
        state.loading = false;
        state.entities = action.payload.data;
        state.totalItems = action.payload.data?.length || 0;
      })
      .addMatcher(isFulfilled(createSousMatiere, updateSousMatiere), (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(deleteSousMatiere), state => {
        state.updating = false;
        state.updateSuccess = true;
      })
      .addMatcher(isPending(getSousMatieres), state => {
        state.errorMessage = null;
        state.loading = true;
      })
      .addMatcher(isPending(createSousMatiere, updateSousMatiere, deleteSousMatiere), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = SousMatiereSlice.actions;

export default SousMatiereSlice.reducer;
